"use client";

import { c as _c } from "react/compiler-runtime";
import { Chat, useChat } from "@ai-sdk/react";
import { createCodePlugin } from "@streamdown/code";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DefaultChatTransport } from "ai";
import { MessageCircle, X, Send, RotateCcw, UserRound, Loader2, ThumbsUp, ThumbsDown, Star, CheckCircle2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Streamdown } from "streamdown";
import { useUser } from "@/hooks/useUser";
import { Button } from "@/lib/components/button";
import { useFetchClient } from "@/lib/fetch-client";
import { cn } from "@/lib/utils";
import { useI18n } from "@llmgateway/i18n/react";
import type { UIMessage } from "ai";
import type { LinkSafetyConfig } from "streamdown";
const code = createCodePlugin({
  themes: ["github-light", "github-dark"]
});
const linkSafety: LinkSafetyConfig = {
  enabled: true,
  onLinkCheck: (url: string) => {
    try {
      const hostname = new URL(url).hostname;
      return hostname === "doteb.com" || hostname.endsWith(".doteb.com");
    } catch {
      return false;
    }
  }
};
const ESCALATION_THRESHOLD = 3;
const CLIENT_ID_KEY = "chat_support_client_id";
interface ConversationMessage {
  id: string;
  role: "user" | "assistant" | "admin";
  content: string;
  sequence: number;
  reaction: "like" | "dislike" | null;
}
interface ConversationData {
  conversationId: string | null;
  messages: ConversationMessage[];
  resolvedAt: string | null;
  rating: number | null;
  escalatedAt: string | null;
}
interface MessageMeta {
  sequence?: number;
  admin?: boolean;
}
function createClientId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = bytes[6] & 0x0f | 0x40;
    bytes[8] = bytes[8] & 0x3f | 0x80;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
  }
  return `client-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

// Persisted in localStorage so a visitor's conversation survives reloads and
// new sessions — that's what lets them keep seeing admin replies over time.
function getOrCreateClientId(): string {
  if (typeof window === "undefined") {
    return "";
  }
  const existing = localStorage.getItem(CLIENT_ID_KEY);
  if (existing) {
    return existing;
  }
  const id = createClientId();
  localStorage.setItem(CLIENT_ID_KEY, id);
  return id;
}
function getTextFromParts(message: UIMessage): string {
  return message.parts.filter((p): p is {
    type: "text";
    text: string;
  } => p.type === "text").map(p => p.text).join("");
}
export function ChatSupport() {
  const $ = _c(183);
  const {
    t
  } = useI18n();
  const fetchClient = useFetchClient();
  const queryClient = useQueryClient();
  const {
    user
  } = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [text, setText] = useState("");
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [hasIdentified, setHasIdentified] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [reactionOverrides, setReactionOverrides] = useState(t0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const [clientId, setClientId] = useState("");
  const [mounted, setMounted] = useState(false);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setClientId(getOrCreateClientId());
      setMounted(true);
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const isLoggedIn = mounted && !!user;
  const effectiveName = isLoggedIn ? user?.name ?? "" : userName;
  const effectiveEmail = isLoggedIn ? user?.email ?? "" : userEmail;
  let t3;
  if ($[3] !== clientId) {
    t3 = ["chat-support-conversation", clientId];
    $[3] = clientId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const conversationQueryKey = t3;
  const t4 = !!clientId && isOpen;
  const t5 = isOpen ? 8000 : false;
  let t6;
  if ($[5] !== clientId || $[6] !== fetchClient) {
    t6 = async () => {
      const res = await fetchClient.GET("/public/chat-support/conversation" as never, {
        params: {
          query: {
            clientId
          }
        }
      } as never);
      if (res.error) {
        throw new Error("Failed to load conversation");
      }
      return res.data as unknown as ConversationData;
    };
    $[5] = clientId;
    $[6] = fetchClient;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== conversationQueryKey || $[9] !== t4 || $[10] !== t5 || $[11] !== t6) {
    t7 = {
      queryKey: conversationQueryKey,
      enabled: t4,
      refetchInterval: t5,
      queryFn: t6
    };
    $[8] = conversationQueryKey;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const {
    data: convData
  } = useQuery(t7);
  let t8;
  if ($[13] !== clientId || $[14] !== effectiveEmail || $[15] !== effectiveName) {
    t8 = new Chat({
      transport: new DefaultChatTransport({
        api: "/api/chat-support",
        body: {
          name: effectiveName,
          email: effectiveEmail,
          clientId
        }
      })
    });
    $[13] = clientId;
    $[14] = effectiveEmail;
    $[15] = effectiveName;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const chat = t8;
  let t9;
  if ($[17] !== conversationQueryKey || $[18] !== queryClient) {
    t9 = () => {
      queryClient.invalidateQueries({
        queryKey: conversationQueryKey
      });
    };
    $[17] = conversationQueryKey;
    $[18] = queryClient;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== chat || $[21] !== t9) {
    t10 = {
      chat,
      onFinish: t9
    };
    $[20] = chat;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  const {
    messages,
    sendMessage,
    status,
    setMessages,
    error
  } = useChat(t10);
  const isLoading = status === "streaming" || status === "submitted";
  let t11;
  let t12;
  if ($[23] !== convData || $[24] !== messages || $[25] !== setMessages || $[26] !== status) {
    t11 = () => {
      if (!convData || status !== "ready") {
        return;
      }
      const serverMessages = convData.messages;
      if (serverMessages.length === 0) {
        return;
      }
      const allSeeded = messages.every(_temp);
      if (serverMessages.length > messages.length || serverMessages.length === messages.length && !allSeeded) {
        setMessages(serverMessages.map(_temp2));
      }
    };
    t12 = [convData, status, messages, setMessages];
    $[23] = convData;
    $[24] = messages;
    $[25] = setMessages;
    $[26] = status;
    $[27] = t11;
    $[28] = t12;
  } else {
    t11 = $[27];
    t12 = $[28];
  }
  useEffect(t11, t12);
  let t13;
  if ($[29] !== convData?.escalatedAt) {
    t13 = () => {
      if (convData?.escalatedAt) {
        setEscalated(true);
      }
    };
    $[29] = convData?.escalatedAt;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const t14 = convData?.escalatedAt;
  let t15;
  if ($[31] !== t14) {
    t15 = [t14];
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  useEffect(t13, t15);
  convData?.messages;
  let map;
  if ($[33] !== convData?.messages) {
    map = {};
    for (const m_1 of convData?.messages ?? []) {
      if (m_1.role === "assistant" && m_1.reaction) {
        map[m_1.sequence] = m_1.reaction;
      }
    }
    $[33] = convData?.messages;
    $[34] = map;
  } else {
    map = $[34];
  }
  const reactionBySequence = map;
  const isResolved = !!convData?.resolvedAt;
  const hasConversation = (convData?.messages.length ?? 0) > 0;
  const isIdentified = isLoggedIn || hasIdentified || hasConversation;
  let t16;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: "smooth"
      });
    };
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== messages) {
    t17 = [messages];
    $[36] = messages;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  useEffect(t16, t17);
  let t18;
  let t19;
  if ($[38] !== isIdentified || $[39] !== isOpen) {
    t18 = () => {
      if (isOpen && isIdentified && inputRef.current) {
        inputRef.current.focus();
      }
    };
    t19 = [isOpen, isIdentified];
    $[38] = isIdentified;
    $[39] = isOpen;
    $[40] = t18;
    $[41] = t19;
  } else {
    t18 = $[40];
    t19 = $[41];
  }
  useEffect(t18, t19);
  let t20;
  let t21;
  if ($[42] !== isOpen || $[43] !== messages) {
    t20 = () => {
      if (!isOpen && messages.length > prevMessageCountRef.current && messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.role === "assistant") {
          setHasUnread(true);
        }
      }
      prevMessageCountRef.current = messages.length;
    };
    t21 = [isOpen, messages];
    $[42] = isOpen;
    $[43] = messages;
    $[44] = t20;
    $[45] = t21;
  } else {
    t20 = $[44];
    t21 = $[45];
  }
  useEffect(t20, t21);
  let t22;
  let t23;
  if ($[46] !== isOpen) {
    t22 = () => {
      if (typeof document === "undefined") {
        return;
      }
      if (!isOpen) {
        return;
      }
      const mq = window.matchMedia("(max-width: 639px)");
      if (!mq.matches) {
        return;
      }
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    };
    t23 = [isOpen];
    $[46] = isOpen;
    $[47] = t22;
    $[48] = t23;
  } else {
    t22 = $[47];
    t23 = $[48];
  }
  useEffect(t22, t23);
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => {
      setIsOpen(true);
      setHasUnread(false);
    };
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  const handleOpen = t24;
  let t25;
  if ($[50] !== messages) {
    t25 = messages.filter(_temp3);
    $[50] = messages;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  const userMessageCount = t25.length;
  const showEscalation = !escalated && userMessageCount >= ESCALATION_THRESHOLD;
  const canResolve = !isResolved && userMessageCount >= 1;
  let t26;
  if ($[52] !== clientId || $[53] !== effectiveEmail || $[54] !== effectiveName || $[55] !== fetchClient || $[56] !== messages) {
    t26 = async () => {
      const res_0 = await fetchClient.POST("/public/chat-support/escalate" as never, {
        body: {
          name: effectiveName,
          email: effectiveEmail,
          clientId,
          messages: messages.map(_temp4)
        }
      } as never);
      if (res_0.error) {
        throw new Error("Escalation failed");
      }
      return res_0.data;
    };
    $[52] = clientId;
    $[53] = effectiveEmail;
    $[54] = effectiveName;
    $[55] = fetchClient;
    $[56] = messages;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  let t27;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => {
      setEscalated(true);
    };
    $[58] = t27;
  } else {
    t27 = $[58];
  }
  let t28;
  if ($[59] !== t26) {
    t28 = {
      mutationFn: t26,
      onSuccess: t27
    };
    $[59] = t26;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  const escalateMutation = useMutation(t28);
  let t29;
  if ($[61] !== clientId || $[62] !== fetchClient) {
    t29 = async vars => {
      const res_1 = await fetchClient.POST("/public/chat-support/reaction" as never, {
        body: {
          clientId,
          ...vars
        }
      } as never);
      if (res_1.error) {
        throw new Error("Reaction failed");
      }
      return res_1.data;
    };
    $[61] = clientId;
    $[62] = fetchClient;
    $[63] = t29;
  } else {
    t29 = $[63];
  }
  let t30;
  if ($[64] !== conversationQueryKey || $[65] !== queryClient) {
    t30 = () => {
      queryClient.invalidateQueries({
        queryKey: conversationQueryKey
      });
    };
    $[64] = conversationQueryKey;
    $[65] = queryClient;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t29 || $[68] !== t30) {
    t31 = {
      mutationFn: t29,
      onSuccess: t30
    };
    $[67] = t29;
    $[68] = t30;
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  const reactionMutation = useMutation(t31);
  let t32;
  if ($[70] !== clientId || $[71] !== fetchClient) {
    t32 = async rating => {
      const res_2 = await fetchClient.POST("/public/chat-support/resolve" as never, {
        body: {
          clientId,
          rating
        }
      } as never);
      if (res_2.error) {
        throw new Error("Resolve failed");
      }
      return res_2.data;
    };
    $[70] = clientId;
    $[71] = fetchClient;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== conversationQueryKey || $[74] !== queryClient) {
    t33 = () => {
      setShowRating(false);
      queryClient.invalidateQueries({
        queryKey: conversationQueryKey
      });
    };
    $[73] = conversationQueryKey;
    $[74] = queryClient;
    $[75] = t33;
  } else {
    t33 = $[75];
  }
  let t34;
  if ($[76] !== t32 || $[77] !== t33) {
    t34 = {
      mutationFn: t32,
      onSuccess: t33
    };
    $[76] = t32;
    $[77] = t33;
    $[78] = t34;
  } else {
    t34 = $[78];
  }
  const resolveMutation = useMutation(t34);
  let t35;
  if ($[79] !== reactionBySequence || $[80] !== reactionMutation || $[81] !== reactionOverrides) {
    t35 = (sequence, reaction) => {
      const current = reactionOverrides[sequence] ?? reactionBySequence[sequence];
      const next = current === reaction ? null : reaction;
      setReactionOverrides(prev => {
        const updated = {};
        for (const [key, value] of Object.entries(prev)) {
          if (Number(key) !== sequence) {
            updated[Number(key)] = value;
          }
        }
        if (next) {
          updated[sequence] = next;
        }
        return updated;
      });
      reactionMutation.mutate({
        sequence,
        reaction: next
      });
    };
    $[79] = reactionBySequence;
    $[80] = reactionMutation;
    $[81] = reactionOverrides;
    $[82] = t35;
  } else {
    t35 = $[82];
  }
  const handleReact = t35;
  let t36;
  if ($[83] !== isLoggedIn || $[84] !== setMessages) {
    t36 = () => {
      setMessages([]);
      setEscalated(false);
      setShowRating(false);
      setReactionOverrides({});
      const newId = createClientId();
      if (typeof window !== "undefined") {
        localStorage.setItem(CLIENT_ID_KEY, newId);
      }
      setClientId(newId);
      if (!isLoggedIn) {
        setHasIdentified(false);
        setUserName("");
        setUserEmail("");
      }
    };
    $[83] = isLoggedIn;
    $[84] = setMessages;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  const handleReset = t36;
  let t37;
  if ($[86] !== userName) {
    t37 = e => {
      e.preventDefault();
      if (!userName.trim()) {
        return;
      }
      setHasIdentified(true);
    };
    $[86] = userName;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  const handleIdentify = t37;
  let t38;
  if ($[88] !== isLoading || $[89] !== sendMessage || $[90] !== text) {
    t38 = e_0 => {
      e_0.preventDefault();
      const trimmed = text.trim();
      if (!trimmed || isLoading) {
        return;
      }
      sendMessage({
        text: trimmed
      });
      setText("");
    };
    $[88] = isLoading;
    $[89] = sendMessage;
    $[90] = text;
    $[91] = t38;
  } else {
    t38 = $[91];
  }
  const handleSubmit = t38;
  let t39;
  if ($[92] !== handleSubmit) {
    t39 = e_1 => {
      if (e_1.key === "Enter" && !e_1.shiftKey) {
        e_1.preventDefault();
        handleSubmit(e_1);
      }
    };
    $[92] = handleSubmit;
    $[93] = t39;
  } else {
    t39 = $[93];
  }
  const handleKeyDown = t39;
  const t40 = !isOpen;
  const t41 = !isOpen;
  const t42 = isOpen ? "visible translate-y-0 opacity-100" : "invisible pointer-events-none translate-y-4 opacity-0";
  let t43;
  if ($[94] !== t42) {
    t43 = cn("fixed z-[60] flex flex-col overflow-hidden border-border bg-background shadow-2xl transition-all duration-200 ease-out", "inset-0 rounded-none border-0", "sm:inset-auto sm:bottom-[calc(5rem+env(safe-area-inset-bottom,0px))] sm:right-6 sm:h-[min(32rem,calc(100svh-7rem))] sm:w-[24rem] sm:rounded-xl sm:border", t42);
    $[94] = t42;
    $[95] = t43;
  } else {
    t43 = $[95];
  }
  let t44;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = {
      paddingTop: "max(env(safe-area-inset-top, 0px), 0.75rem)"
    };
    $[96] = t44;
  } else {
    t44 = $[96];
  }
  let t45;
  if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <div className="flex size-8 items-center justify-center rounded-full bg-primary-foreground/20"><MessageCircle className="size-4 text-primary-foreground" /></div>;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t) {
    t46 = t("chatSupport.title");
    $[98] = t;
    $[99] = t46;
  } else {
    t46 = $[99];
  }
  let t47;
  if ($[100] !== t46) {
    t47 = <h3 className="text-sm font-semibold text-primary-foreground">{t46}</h3>;
    $[100] = t46;
    $[101] = t47;
  } else {
    t47 = $[101];
  }
  let t48;
  if ($[102] !== t) {
    t48 = t("chatSupport.subtitle");
    $[102] = t;
    $[103] = t48;
  } else {
    t48 = $[103];
  }
  let t49;
  if ($[104] !== t48) {
    t49 = <p className="text-xs text-primary-foreground/70">{t48}</p>;
    $[104] = t48;
    $[105] = t49;
  } else {
    t49 = $[105];
  }
  let t50;
  if ($[106] !== t47 || $[107] !== t49) {
    t50 = <div className="flex items-center gap-2.5">{t45}<div className="leading-tight">{t47}{t49}</div></div>;
    $[106] = t47;
    $[107] = t49;
    $[108] = t50;
  } else {
    t50 = $[108];
  }
  let t51;
  if ($[109] !== t) {
    t51 = t("chatSupport.newConversation");
    $[109] = t;
    $[110] = t51;
  } else {
    t51 = $[110];
  }
  let t52;
  if ($[111] !== t) {
    t52 = t("chatSupport.newConversation");
    $[111] = t;
    $[112] = t52;
  } else {
    t52 = $[112];
  }
  let t53;
  let t54;
  if ($[113] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = {
      touchAction: "manipulation"
    };
    t54 = <RotateCcw className="size-4" />;
    $[113] = t53;
    $[114] = t54;
  } else {
    t53 = $[113];
    t54 = $[114];
  }
  let t55;
  if ($[115] !== handleReset || $[116] !== t51 || $[117] !== t52) {
    t55 = <button type="button" onClick={handleReset} className="flex size-9 items-center justify-center rounded-md text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground" aria-label={t51} title={t52} style={t53}>{t54}</button>;
    $[115] = handleReset;
    $[116] = t51;
    $[117] = t52;
    $[118] = t55;
  } else {
    t55 = $[118];
  }
  let t56;
  if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = () => setIsOpen(false);
    $[119] = t56;
  } else {
    t56 = $[119];
  }
  let t57;
  if ($[120] !== t) {
    t57 = t("chatSupport.closeChat");
    $[120] = t;
    $[121] = t57;
  } else {
    t57 = $[121];
  }
  let t58;
  let t59;
  if ($[122] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = {
      touchAction: "manipulation"
    };
    t59 = <X className="size-4" />;
    $[122] = t58;
    $[123] = t59;
  } else {
    t58 = $[122];
    t59 = $[123];
  }
  let t60;
  if ($[124] !== t57) {
    t60 = <button type="button" onClick={t56} className="flex size-9 items-center justify-center rounded-md text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground" aria-label={t57} style={t58}>{t59}</button>;
    $[124] = t57;
    $[125] = t60;
  } else {
    t60 = $[125];
  }
  let t61;
  if ($[126] !== t55 || $[127] !== t60) {
    t61 = <div className="flex items-center gap-1">{t55}{t60}</div>;
    $[126] = t55;
    $[127] = t60;
    $[128] = t61;
  } else {
    t61 = $[128];
  }
  let t62;
  if ($[129] !== t50 || $[130] !== t61) {
    t62 = <div className="flex shrink-0 items-center justify-between border-b border-border bg-primary px-4 py-3" style={t44}>{t50}{t61}</div>;
    $[129] = t50;
    $[130] = t61;
    $[131] = t62;
  } else {
    t62 = $[131];
  }
  let t63;
  if ($[132] !== canResolve || $[133] !== convData || $[134] !== effectiveName || $[135] !== error || $[136] !== escalateMutation || $[137] !== escalated || $[138] !== handleIdentify || $[139] !== handleKeyDown || $[140] !== handleReact || $[141] !== handleSubmit || $[142] !== hoverRating || $[143] !== isIdentified || $[144] !== isLoading || $[145] !== isResolved || $[146] !== messages || $[147] !== reactionBySequence || $[148] !== reactionOverrides || $[149] !== resolveMutation || $[150] !== showEscalation || $[151] !== showRating || $[152] !== t || $[153] !== text || $[154] !== userEmail || $[155] !== userName) {
    t63 = !isIdentified ? <div className="flex flex-1 flex-col justify-center px-6 py-8"><div className="mb-6 text-center"><h4 className="text-base font-semibold text-foreground">{t("chatSupport.welcome")}</h4><p className="mt-1 text-sm text-muted-foreground">{t("chatSupport.introduce")}</p></div><form onSubmit={handleIdentify} className="flex flex-col gap-3"><input type="text" value={userName} onChange={e_2 => setUserName(e_2.target.value)} placeholder={t("chatSupport.namePlaceholder")} required={true} className="rounded-lg border border-input bg-background px-3 py-2.5 text-base outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring sm:text-sm" autoFocus={true} /><input type="email" value={userEmail} onChange={e_3 => setUserEmail(e_3.target.value)} placeholder={t("chatSupport.emailPlaceholder")} className="rounded-lg border border-input bg-background px-3 py-2.5 text-base outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring sm:text-sm" /><Button type="submit" disabled={!userName.trim()} className="mt-1 w-full">{t("chatSupport.startChat")}</Button></form></div> : <><div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4"><div className="flex flex-col gap-3">{messages.length === 0 && <div className="flex justify-start"><div className="max-w-[85%] rounded-2xl bg-muted px-3.5 py-2.5 text-sm leading-relaxed text-foreground">{t("chatSupport.initialMessage", {
                name: effectiveName ? ` ${effectiveName}` : ""
              })}</div></div>}{messages.map((message, index) => {
            const content = getTextFromParts(message);
            if (!content) {
              return null;
            }
            const meta = (message.metadata ?? {}) as MessageMeta;
            const isAdmin = meta.admin === true;
            const isAssistant = message.role === "assistant" && !isAdmin;
            const sequence_0 = meta.sequence ?? index;
            const isPersisted = meta.sequence !== undefined;
            const reaction_0 = reactionOverrides[sequence_0] ?? reactionBySequence[sequence_0];
            const isLastAssistant = isAssistant && message.id === messages[messages.length - 1]?.id;
            return <div key={message.id} className={cn("flex flex-col gap-1", message.role === "user" ? "items-end" : "items-start")}>{isAdmin && <span className="px-1 text-[11px] font-medium text-blue-600 dark:text-blue-400">{t("chatSupport.supportTeam")}</span>}<div className={cn("max-w-[85%] overflow-hidden rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed break-words", isAdmin ? "bg-blue-600 text-white dark:bg-blue-700" : isAssistant ? "bg-muted text-foreground" : "bg-primary text-primary-foreground")}>{isAssistant ? <Streamdown isAnimating={isLastAssistant && isLoading} controls={false} plugins={{
                  code
                }} linkSafety={linkSafety} className="overflow-x-auto [&_pre]:overflow-x-auto [&_code]:break-all [&_ul]:pl-5 [&_ol]:pl-5">{content}</Streamdown> : content}</div>{isAssistant && isPersisted && <div className="flex items-center gap-1 px-1"><button type="button" onClick={() => handleReact(sequence_0, "like")} aria-label={t("chatSupport.helpful")} className={cn("flex size-6 items-center justify-center rounded-md transition-colors hover:bg-muted", reaction_0 === "like" ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground")} style={{
                  touchAction: "manipulation"
                }}><ThumbsUp className="size-3.5" /></button><button type="button" onClick={() => handleReact(sequence_0, "dislike")} aria-label={t("chatSupport.notHelpful")} className={cn("flex size-6 items-center justify-center rounded-md transition-colors hover:bg-muted", reaction_0 === "dislike" ? "text-rose-600 dark:text-rose-400" : "text-muted-foreground")} style={{
                  touchAction: "manipulation"
                }}><ThumbsDown className="size-3.5" /></button></div>}</div>;
          })}{isLoading && (messages.length === 0 || messages[messages.length - 1]?.role !== "assistant" || !getTextFromParts(messages[messages.length - 1])) && <div className="flex justify-start"><div className="flex items-center gap-1.5 rounded-2xl bg-muted px-3.5 py-3"><div className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.3s]" /><div className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50 [animation-delay:-0.15s]" /><div className="size-1.5 animate-bounce rounded-full bg-muted-foreground/50" /></div></div>}{error && <div className="flex justify-start"><div className="max-w-[85%] rounded-2xl bg-destructive/10 px-3.5 py-2.5 text-sm leading-relaxed text-destructive"><p>{t("chatSupport.error")}</p>{error.message && <p className="mt-1 text-xs opacity-80 break-words">{error.message}</p>}</div></div>}<div ref={messagesEndRef} /></div></div>{isResolved ? <div className="shrink-0 border-t border-border bg-emerald-50 px-4 py-2.5 dark:bg-emerald-950/30"><div className="flex items-center gap-1.5"><CheckCircle2 className="size-3.5 text-emerald-600 dark:text-emerald-400" /><p className="text-xs text-emerald-700 dark:text-emerald-400">{t("chatSupport.resolved")}</p></div>{(convData?.rating ?? 0) > 0 && <div className="mt-1 flex items-center gap-0.5">{Array.from({
            length: 5
          }).map((_, i) => <Star key={i} className={cn("size-3.5", i < (convData.rating ?? 0) ? "fill-amber-400 text-amber-400" : "fill-none text-muted-foreground/40")} />)}</div>}</div> : showRating ? <div className="shrink-0 border-t border-border bg-muted/50 px-4 py-3"><p className="mb-2 text-xs text-muted-foreground">{t("chatSupport.rateConversation")}</p><div className="flex items-center gap-1">{Array.from({
            length: 5
          }).map((__0, i_0) => {
            const value_0 = i_0 + 1;
            return <button key={value_0} type="button" onMouseEnter={() => setHoverRating(value_0)} onMouseLeave={() => setHoverRating(0)} onClick={() => resolveMutation.mutate(value_0)} disabled={resolveMutation.isPending} aria-label={t("chatSupport.starRating", {
              count: value_0
            })} className="flex size-8 items-center justify-center" style={{
              touchAction: "manipulation"
            }}><Star className={cn("size-6 transition-colors", value_0 <= hoverRating ? "fill-amber-400 text-amber-400" : "fill-none text-muted-foreground/50")} /></button>;
          })}</div><button type="button" onClick={() => resolveMutation.mutate(0)} disabled={resolveMutation.isPending} className="mt-2 text-xs text-muted-foreground underline hover:text-foreground">{t("chatSupport.resolveWithoutRating")}</button></div> : (showEscalation || canResolve) && <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-border bg-muted/50 px-4 py-2.5">{showEscalation && <button type="button" onClick={() => escalateMutation.mutate()} disabled={escalateMutation.isPending} className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50" style={{
          touchAction: "manipulation"
        }}>{escalateMutation.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <UserRound className="size-3.5" />}{t("chatSupport.talkToHuman")}</button>}{canResolve && <button type="button" onClick={() => setShowRating(true)} className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted" style={{
          touchAction: "manipulation"
        }}><CheckCircle2 className="size-3.5" />{t("chatSupport.resolve")}</button>}</div>}{escalated && !isResolved && <div className="shrink-0 border-t border-border bg-green-50 px-4 py-2.5 dark:bg-green-950/30"><p className="text-xs text-green-700 dark:text-green-400">{t("chatSupport.escalated")}</p></div>}<div className="shrink-0 border-t border-border bg-background p-3" style={{
        paddingBottom: "max(env(safe-area-inset-bottom, 0px), 0.75rem)"
      }}><form onSubmit={handleSubmit} className="flex items-end gap-2"><textarea ref={inputRef} value={text} onChange={e_4 => setText(e_4.target.value)} onKeyDown={handleKeyDown} placeholder={t("chatSupport.messagePlaceholder")} rows={1} className="field-sizing-content max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-base outline-none transition-colors placeholder:text-muted-foreground focus:border-ring focus:ring-1 focus:ring-ring sm:text-sm" /><Button type="submit" size="icon" disabled={!text.trim() || isLoading} className="size-10 shrink-0 rounded-lg"><Send className="size-4" /><span className="sr-only">{t("chatSupport.sendMessage")}</span></Button></form></div></>;
    $[132] = canResolve;
    $[133] = convData;
    $[134] = effectiveName;
    $[135] = error;
    $[136] = escalateMutation;
    $[137] = escalated;
    $[138] = handleIdentify;
    $[139] = handleKeyDown;
    $[140] = handleReact;
    $[141] = handleSubmit;
    $[142] = hoverRating;
    $[143] = isIdentified;
    $[144] = isLoading;
    $[145] = isResolved;
    $[146] = messages;
    $[147] = reactionBySequence;
    $[148] = reactionOverrides;
    $[149] = resolveMutation;
    $[150] = showEscalation;
    $[151] = showRating;
    $[152] = t;
    $[153] = text;
    $[154] = userEmail;
    $[155] = userName;
    $[156] = t63;
  } else {
    t63 = $[156];
  }
  let t64;
  if ($[157] !== t40 || $[158] !== t41 || $[159] !== t43 || $[160] !== t62 || $[161] !== t63) {
    t64 = <div aria-hidden={t40} inert={t41} className={t43}>{t62}{t63}</div>;
    $[157] = t40;
    $[158] = t41;
    $[159] = t43;
    $[160] = t62;
    $[161] = t63;
    $[162] = t64;
  } else {
    t64 = $[162];
  }
  let t65;
  if ($[163] !== isOpen) {
    t65 = isOpen ? () => setIsOpen(false) : handleOpen;
    $[163] = isOpen;
    $[164] = t65;
  } else {
    t65 = $[164];
  }
  let t66;
  if ($[165] === Symbol.for("react.memo_cache_sentinel")) {
    t66 = {
      bottom: "calc(1rem + env(safe-area-inset-bottom, 0px))",
      right: "calc(1rem + env(safe-area-inset-right, 0px))",
      touchAction: "manipulation"
    };
    $[165] = t66;
  } else {
    t66 = $[165];
  }
  const t67 = isOpen && "rotate-90";
  const t68 = isOpen && "hidden sm:flex";
  let t69;
  if ($[166] !== t67 || $[167] !== t68) {
    t69 = cn("fixed z-[70] flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all duration-200 hover:bg-primary/90 hover:shadow-xl active:scale-95 sm:!right-6 sm:flex", t67, t68);
    $[166] = t67;
    $[167] = t68;
    $[168] = t69;
  } else {
    t69 = $[168];
  }
  let t70;
  if ($[169] !== isOpen || $[170] !== t) {
    t70 = isOpen ? t("chatSupport.closeChat") : t("chatSupport.openChat");
    $[169] = isOpen;
    $[170] = t;
    $[171] = t70;
  } else {
    t70 = $[171];
  }
  let t71;
  if ($[172] !== hasUnread || $[173] !== isOpen) {
    t71 = isOpen ? <X className="size-5" /> : <><MessageCircle className="size-5" />{hasUnread && <span className="absolute -right-0.5 -top-0.5 flex size-3"><span className="absolute inline-flex size-full animate-ping rounded-full bg-destructive opacity-75" /><span className="relative inline-flex size-3 rounded-full bg-destructive" /></span>}</>;
    $[172] = hasUnread;
    $[173] = isOpen;
    $[174] = t71;
  } else {
    t71 = $[174];
  }
  let t72;
  if ($[175] !== t65 || $[176] !== t69 || $[177] !== t70 || $[178] !== t71) {
    t72 = <button type="button" onClick={t65} style={t66} className={t69} aria-label={t70}>{t71}</button>;
    $[175] = t65;
    $[176] = t69;
    $[177] = t70;
    $[178] = t71;
    $[179] = t72;
  } else {
    t72 = $[179];
  }
  let t73;
  if ($[180] !== t64 || $[181] !== t72) {
    t73 = <>{t64}{t72}</>;
    $[180] = t64;
    $[181] = t72;
    $[182] = t73;
  } else {
    t73 = $[182];
  }
  return t73;
}
function _temp4(m_3) {
  return {
    role: m_3.role,
    content: getTextFromParts(m_3)
  };
}
function _temp3(m_2) {
  return m_2.role === "user";
}
function _temp2(m_0) {
  return {
    id: `srv-${m_0.sequence}`,
    role: m_0.role === "user" ? "user" : "assistant",
    parts: [{
      type: "text",
      text: m_0.content
    }],
    metadata: {
      sequence: m_0.sequence,
      admin: m_0.role === "admin"
    }
  };
}
function _temp(m) {
  return (m.metadata as MessageMeta | undefined)?.sequence !== undefined;
}